import anime from "animejs";
import { useEffect, useRef, useMemo, memo } from "react";
import { Link } from "react-router";

import type { PeakLabelAnimationProps } from "../types/components/PeakLabelAnimationProps";

import "../assets/scss/components/_peak-label-animation.scss";
import { DateDifference } from "../utilities/DateUtils";

const PeakLabelAnimationComponent = ({
	text = "",
	date = false,
	link = "/next-day-flowers",
}: PeakLabelAnimationProps) => {
	const hasAnimated = useRef(false);

	const containerRef = useRef(null);

	useEffect(() => {
		if (hasAnimated.current) return;

		if (!containerRef.current) return;

		const container = containerRef.current;
		const letters = container.querySelectorAll(".peak-label-letter");

		setTimeout(() => {
			// Create the timeline
			const tl = anime.timeline({
				loop: false,
				begin: () => {
					hasAnimated.current = true;
				},
			});

			tl.add({
				targets: container,
				maxHeight: [0, 100],
				opacity: [0, 1],
				duration: 1000,
				easing: "easeOutQuad",
			});

			tl.add({
				targets: letters,
				translateY: ["1.5em", 0],
				color: "white",
				translateZ: 0,
				duration: 750,
				delay: (el, i) => 50 * i,
				begin: () => {
					hasAnimated.current = true;
				},
			});
		}, 500);

		return () => {
			anime.remove(letters);
		};
	}, []);

	const { dateSpecificText, shouldRender } = useMemo(() => {
		if (text === "" || !date) return { shouldRender: false };

		let fullDate = new Date(date);
		fullDate.setFullYear(new Date().getFullYear());

		const daysLeft = fullDate ? DateDifference(fullDate).days + 1 : null;

		if (daysLeft < 0) return { shouldRender: false };

		let calculatedText = daysLeft ? `${daysLeft} days until ` : "";
		calculatedText = daysLeft === 1 ? "Last chance to order for " : calculatedText;

		return { shouldRender: true, dateSpecificText: calculatedText };
	}, [text, date]);

	if (!shouldRender) return null;

	return (
		<Link to={link} className="peak-label-animation" ref={containerRef}>
			<span className="peak-label-text-wrapper">
				<span className="peak-label-letters">
					{dateSpecificText.split("").map((char, index) => {
						return (
							<span
								key={index}
								className={`peak-label-letter date-specific ${char == " " ? "space" : ""}`}
							>
								{char !== " " ? char : "/"}
							</span>
						);
					})}

					{text.split("").map((char, index) => {
						return (
							<span key={index} className={`peak-label-letter ${char == " " ? "space" : ""}`}>
								{char !== " " ? char : "/"}
							</span>
						);
					})}
				</span>
			</span>
		</Link>
	);
};

export const PeakLabelAnimation = memo(PeakLabelAnimationComponent);
